import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { components } from "../../../shared/schema/schema";
import ServicePlanViolationList from "./ServicePlanViolationList";

type CoServicePlanImportViolation = components["schemas"]["CoServicePlanImportViolation"];

interface Props {
  isOpen: boolean;
  isSubmitting: boolean;
  violations: CoServicePlanImportViolation[];
  onKeepEditing: () => void;
  onSubmitAnyway: () => void;
}

/**
 * What the verifier made of the week a coordinator built, asked as a question.
 *
 * Reaching this is not a failure and nothing has been written: the submit posted
 * with `acceptViolations: false`, which is the call that asks. **Submit anyway**
 * posts the same week again with it true, and **Keep editing** goes back to the grid
 * with the draft untouched.
 *
 * Errors first and set apart from warnings, because they are different questions.
 * A warning is a caveat carried forward on a week that would have been written
 * anyway; an error is a rule the schedulers themselves are refused for breaking, and
 * proceeding past one is a coordinator overruling it in their own name. That name is
 * recorded, and the drawer shows it to whoever approves — which is usually somebody
 * else.
 */
export default function ScheduleEditorConfirmDialog(props: Props) {
  const keepEditingRef = React.useRef<HTMLButtonElement>(null);

  const errors = props.violations.filter((violation) => violation.severity === "ERROR");
  const warnings = props.violations.filter((violation) => violation.severity !== "ERROR");

  return (
    <AlertDialog
      closeOnEsc={!props.isSubmitting}
      closeOnOverlayClick={!props.isSubmitting}
      isOpen={props.isOpen}
      leastDestructiveRef={keepEditingRef}
      scrollBehavior="inside"
      size="2xl"
      onClose={props.onKeepEditing}
    >
      {/* Over the editor modal this opened from, which is itself pinned above the
          legacy AngularJS footer. At equal z-index the later portal wins, and this
          one is created after the modal's. */}
      <AlertDialogOverlay zIndex="aboveLegacyFooter" />
      <AlertDialogContent containerProps={{ zIndex: "aboveLegacyFooter" }}>
        <AlertDialogHeader>
          {errors.length > 0
            ? "This week breaks rules a generated one would be refused for"
            : "This week came back with warnings"}
        </AlertDialogHeader>

        <AlertDialogBody>
          <VStack align="stretch" spacing={5}>
            <Text fontSize="sm">
              Nothing has been saved yet. Your week was checked against this plan and the
              authorizations that pay for it, and this is what came back. Submitting accepts these
              knowingly: your name and what you accepted are recorded and shown to whoever approves
              the submission.
            </Text>

            {errors.length > 0 && (
              <Box borderLeftColor="red.500" borderLeftWidth="4px" pl={4}>
                <Heading color="red.600" mb={1} size="sm">
                  Blocking findings ({errors.length})
                </Heading>
                <Text color="gray.600" fontSize="sm" mb={3}>
                  A week the scheduler built carrying any of these would have been thrown away
                  rather than offered to you. An <b>AUTHORIZATION_LIMIT</b> means the week asks for
                  more than the payer authorized.
                </Text>
                <ServicePlanViolationList violations={errors} />
              </Box>
            )}

            {warnings.length > 0 && (
              <Box borderLeftColor="orange.400" borderLeftWidth="4px" pl={4}>
                <Heading color="orange.600" mb={1} size="sm">
                  Warnings ({warnings.length})
                </Heading>
                <Text color="gray.600" fontSize="sm" mb={3}>
                  These do not stop a week being written. They are kept so what was compromised is
                  on the record.
                </Text>
                <ServicePlanViolationList violations={warnings} />
              </Box>
            )}
          </VStack>
        </AlertDialogBody>

        <AlertDialogFooter gap={3}>
          <Button
            ref={keepEditingRef}
            isDisabled={props.isSubmitting}
            variant="ghost"
            onClick={props.onKeepEditing}
          >
            Keep editing
          </Button>
          <Button
            colorScheme={errors.length > 0 ? "red" : "orange"}
            isLoading={props.isSubmitting}
            onClick={props.onSubmitAnyway}
          >
            Submit anyway
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
